import React from 'react';
import { tripPlanModel } from '../../data/tripPlanModel';

const TripPlanViewer = ({ tripPlan, onClose, onEdit, onSelect, isSelectable = false }) => {
  if (!tripPlan) return null;

  const theme = tripPlanModel.themeOptions.find(t => t.value === tripPlan.tripTheme);
  const status = tripPlanModel.statusOptions.find(s => s.value === tripPlan.status);

  const handleSelect = () => {
    if (isSelectable && onSelect) {
      onSelect(tripPlan);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {tripPlan.tripName}
              </h2>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span>{theme?.icon} {theme?.label}</span>
                <span>•</span>
                <span>{new Date(tripPlan.tripDate).toLocaleDateString('th-TH', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  weekday: 'long'
                })}</span>
                <span>•</span>
                <span>สร้างโดย: {tripPlan.userName}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${status?.color}`}>
                {status?.label}
              </span>
              {tripPlan.isPublic && (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  🌐 สาธารณะ
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">📍 สถานที่</h3>
                <p className="text-gray-900">{tripPlan.tripLocation}</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">👥 ผู้เข้าร่วม</h3>
                <p className="text-gray-900">{tripPlan.participantCount} คน</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">👩 น้องที่ต้องการ</h3>
                <p className="text-gray-900">{tripPlan.requiredGirls} คน</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">⏰ ระยะเวลา</h3>
                <p className="text-gray-900">{tripPlan.duration} วัน</p>
              </div>
              
              {tripPlan.budget > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">💰 งบประมาณ</h3>
                  <p className="text-gray-900">฿{tripPlan.budget.toLocaleString()}</p>
                </div>
              )}
              
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">📅 สร้างเมื่อ</h3>
                <p className="text-gray-900">
                  {new Date(tripPlan.createdAt).toLocaleDateString('th-TH', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Description */}
          {tripPlan.description && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">📝 รายละเอียด</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-900 whitespace-pre-wrap">{tripPlan.description}</p>
              </div>
            </div>
          )}

          {/* Special Requirements */}
          {tripPlan.specialRequirements && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">⭐ ความต้องการพิเศษ</h3>
              <div className="bg-yellow-50 rounded-lg p-4">
                <p className="text-gray-900 whitespace-pre-wrap">{tripPlan.specialRequirements}</p>
              </div>
            </div>
          )}

          {/* Tags */}
          {tripPlan.tags && tripPlan.tags.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">🏷️ Tags</h3>
              <div className="flex flex-wrap gap-2">
                {tripPlan.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Bookings Reference */}
          {tripPlan.bookings && tripPlan.bookings.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">📋 การจองที่เกี่ยวข้อง</h3>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-green-800">
                  มีการจองที่อ้างอิงถึงทริปนี้ {tripPlan.bookings.length} ครั้ง
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-6 bg-gray-50 border-t border-gray-200 flex justify-between">
          <div className="flex gap-3">
            {onEdit && (
              <button
                onClick={onEdit}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
              >
                ✏️ แก้ไข
              </button>
            )}
            {isSelectable && onSelect && (
              <button
                onClick={handleSelect}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
              >
                ✅ เลือกทริปนี้
              </button>
            )}
          </div>
          
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
          >
            ปิด
          </button>
        </div>
      </div>
    </div>
  );
};

export default TripPlanViewer;









